import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  isTrackedSaleInventoryCostSnapshot,
  isTrackedSaleInventoryReturnSnapshot,
  TrackedSaleInventoryCostSnapshotSchema,
  TrackedSaleInventoryReturnSnapshotSchema,
} from "./sale-inventory.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const IdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128, pattern: "^\\S+$" });
const MoneyFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const PositiveQuantitySchema = Type.Integer({ minimum: 1, maximum: 999 });
const CanonicalDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern:
    "^[0-9]{4}-(0[1-9]|1[0-2])-([0-2][0-9]|3[0-1])T([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]\\.[0-9]{3}Z$",
});

export const StorePackageComponentSchema = Type.Union([
  Type.Object(
    {
      kind: Type.Literal("PRODUCT"),
      referenceId: StableIdSchema,
      name: Type.String({ minLength: 1, maxLength: 80 }),
      quantity: PositiveQuantitySchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("SERVICE"),
      referenceId: Type.Union([StableIdSchema, Type.Null()]),
      name: Type.String({ minLength: 1, maxLength: 80 }),
      quantity: PositiveQuantitySchema,
    },
    { additionalProperties: false },
  ),
]);
export type StorePackageComponent = Static<typeof StorePackageComponentSchema>;

export const StorePackageUpsertRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 80 }),
    components: Type.Array(StorePackageComponentSchema, { minItems: 1, maxItems: 30 }),
    listPriceFen: MoneyFenSchema,
    salePriceFen: MoneyFenSchema,
    enabled: Type.Boolean(),
    expectedVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type StorePackageUpsertRequest = Static<typeof StorePackageUpsertRequestSchema>;

export const StorePackageRecordSchema = Type.Object(
  {
    id: StableIdSchema,
    name: Type.String({ minLength: 1, maxLength: 80 }),
    components: Type.Array(StorePackageComponentSchema, { minItems: 1, maxItems: 30 }),
    listPriceFen: MoneyFenSchema,
    salePriceFen: MoneyFenSchema,
    discountFen: MoneyFenSchema,
    enabled: Type.Boolean(),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    createdAt: CanonicalDateTimeSchema,
    updatedAt: CanonicalDateTimeSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type StorePackageRecord = Static<typeof StorePackageRecordSchema>;

export const StorePackageOrderProductComponentSnapshotSchema = Type.Object(
  {
    kind: Type.Literal("PRODUCT"),
    referenceId: StableIdSchema,
    name: Type.String({ minLength: 1, maxLength: 80 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    quantity: PositiveQuantitySchema,
    productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    inventoryManaged: Type.Boolean(),
    inventoryEffect: Type.Union([
      Type.Literal("DEDUCTED_AT_PACKAGE_ADD"),
      Type.Literal("NOT_TRACKED"),
    ]),
    inventoryCostSnapshot: Type.Union([TrackedSaleInventoryCostSnapshotSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type StorePackageOrderProductComponentSnapshot = Static<
  typeof StorePackageOrderProductComponentSnapshotSchema
>;

export const StorePackageOrderServiceComponentSnapshotSchema = Type.Object(
  {
    kind: Type.Literal("SERVICE"),
    referenceId: Type.Union([StableIdSchema, Type.Null()]),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    quantity: PositiveQuantitySchema,
    entitlementEffect: Type.Literal("INCLUDED_CURRENT_ORDER"),
  },
  { additionalProperties: false },
);
export type StorePackageOrderServiceComponentSnapshot = Static<
  typeof StorePackageOrderServiceComponentSnapshotSchema
>;

export const StorePackageOrderComponentSnapshotSchema = Type.Union([
  StorePackageOrderProductComponentSnapshotSchema,
  StorePackageOrderServiceComponentSnapshotSchema,
]);
export type StorePackageOrderComponentSnapshot = Static<
  typeof StorePackageOrderComponentSnapshotSchema
>;

export const StorePackageOrderItemSchema = Type.Object(
  {
    id: StableIdSchema,
    lineNumber: Type.String({ pattern: "^TC-[0-9]{8}-[0-9A-F]{12}$" }),
    orderId: StableIdSchema,
    sessionId: StableIdSchema,
    packageId: StableIdSchema,
    packageVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    packageName: Type.String({ minLength: 1, maxLength: 80 }),
    listPriceFen: MoneyFenSchema,
    discountFen: MoneyFenSchema,
    receivableFen: MoneyFenSchema,
    components: Type.Array(StorePackageOrderComponentSnapshotSchema, {
      minItems: 1,
      maxItems: 30,
    }),
    status: Type.Union([Type.Literal("ACTIVE"), Type.Literal("RETURNED")]),
    createdAt: CanonicalDateTimeSchema,
    returnedAt: Type.Union([CanonicalDateTimeSchema, Type.Null()]),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type StorePackageOrderItem = Static<typeof StorePackageOrderItemSchema>;

export const AddStorePackageOrderItemRequestSchema = Type.Object(
  {
    packageId: StableIdSchema,
    expectedPackageVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    expectedOrderVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type AddStorePackageOrderItemRequest = Static<typeof AddStorePackageOrderItemRequestSchema>;

export const CancelUnpaidStorePackageOrderItemRequestSchema = Type.Object(
  {
    expectedOrderVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    reason: Type.String({ minLength: 2, maxLength: 200, pattern: "^\\S(?:[\\s\\S]*\\S)?$" }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CancelUnpaidStorePackageOrderItemRequest = Static<
  typeof CancelUnpaidStorePackageOrderItemRequestSchema
>;

export const StorePackageProductReturnSnapshotSchema = Type.Object(
  {
    referenceId: StableIdSchema,
    name: Type.String({ minLength: 1, maxLength: 80 }),
    quantity: PositiveQuantitySchema,
    inventoryEffect: Type.Union([
      Type.Literal("SALEABLE_STOCK_RESTORED"),
      Type.Literal("NO_SALEABLE_STOCK_CHANGE"),
      Type.Literal("NOT_TRACKED"),
    ]),
    inventoryReturnSnapshot: Type.Union([TrackedSaleInventoryReturnSnapshotSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type StorePackageProductReturnSnapshot = Static<
  typeof StorePackageProductReturnSnapshotSchema
>;

export const StorePackageOrderItemReturnRecordSchema = Type.Object(
  {
    id: StableIdSchema,
    packageItemId: StableIdSchema,
    packageLineNumber: Type.String({ pattern: "^TC-[0-9]{8}-[0-9A-F]{12}$" }),
    orderId: StableIdSchema,
    sessionId: StableIdSchema,
    returnMode: Type.Union([Type.Literal("UNPAID_CANCEL"), Type.Literal("PAID_APPROVED_REFUND")]),
    orderAmountReductionFen: MoneyFenSchema,
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    productReturns: Type.Array(StorePackageProductReturnSnapshotSchema, { maxItems: 30 }),
    serviceEntitlementEffect: Type.Literal("REVERSED_WITH_PACKAGE"),
    approvalId: Type.Union([StableIdSchema, Type.Null()]),
    occurredAt: CanonicalDateTimeSchema,
    actorId: StableIdSchema,
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type StorePackageOrderItemReturnRecord = Static<
  typeof StorePackageOrderItemReturnRecordSchema
>;

function hasCanonicalTimestamp(value: string): boolean {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

function componentsAreUnique(components: readonly StorePackageComponent[]): boolean {
  const keys = components.map((component) =>
    component.kind === "PRODUCT"
      ? `PRODUCT:${component.referenceId}`
      : `SERVICE:${component.referenceId ?? component.name.trim().toLocaleLowerCase("zh-CN")}`,
  );
  return new Set(keys).size === keys.length;
}

export function isStorePackageUpsertRequest(value: unknown): value is StorePackageUpsertRequest {
  if (!Value.Check(StorePackageUpsertRequestSchema, value)) return false;
  const request = value as StorePackageUpsertRequest;
  return (
    request.name.trim() === request.name &&
    request.salePriceFen <= request.listPriceFen &&
    request.components.every((component) => component.name.trim() === component.name) &&
    componentsAreUnique(request.components)
  );
}

export function isStorePackageRecord(value: unknown): value is StorePackageRecord {
  if (!Value.Check(StorePackageRecordSchema, value)) return false;
  const record = value as StorePackageRecord;
  return (
    record.discountFen === record.listPriceFen - record.salePriceFen &&
    record.salePriceFen <= record.listPriceFen &&
    hasCanonicalTimestamp(record.createdAt) &&
    hasCanonicalTimestamp(record.updatedAt) &&
    record.components.every((component) => component.name.trim() === component.name) &&
    componentsAreUnique(record.components)
  );
}

export function isStorePackageOrderItem(value: unknown): value is StorePackageOrderItem {
  if (!Value.Check(StorePackageOrderItemSchema, value)) return false;
  const item = value as StorePackageOrderItem;
  if (
    item.discountFen !== item.listPriceFen - item.receivableFen ||
    item.receivableFen > item.listPriceFen ||
    !hasCanonicalTimestamp(item.createdAt) ||
    (item.status === "ACTIVE" ? item.returnedAt !== null : item.returnedAt === null)
  ) {
    return false;
  }
  const productIds = new Set<string>();
  for (const component of item.components) {
    if (component.kind === "SERVICE") continue;
    if (productIds.has(component.referenceId)) return false;
    productIds.add(component.referenceId);
    if (
      component.inventoryManaged !== (component.inventoryEffect === "DEDUCTED_AT_PACKAGE_ADD") ||
      (component.inventoryManaged
        ? !isTrackedSaleInventoryCostSnapshot(component.inventoryCostSnapshot) ||
          component.inventoryCostSnapshot.skuId !== component.referenceId ||
          component.inventoryCostSnapshot.productVersion !== component.productVersion ||
          component.inventoryCostSnapshot.soldBaseQuantity !== component.quantity
        : component.inventoryCostSnapshot !== null)
    ) {
      return false;
    }
  }
  return true;
}

export function isStorePackageOrderItemReturnRecord(
  value: unknown,
): value is StorePackageOrderItemReturnRecord {
  if (!Value.Check(StorePackageOrderItemReturnRecordSchema, value)) return false;
  const record = value as StorePackageOrderItemReturnRecord;
  return (
    hasCanonicalTimestamp(record.occurredAt) &&
    (record.returnMode === "UNPAID_CANCEL"
      ? record.approvalId === null
      : record.approvalId !== null) &&
    record.productReturns.every((returned) => {
      if (returned.inventoryEffect === "SALEABLE_STOCK_RESTORED") {
        return (
          isTrackedSaleInventoryReturnSnapshot(returned.inventoryReturnSnapshot) &&
          returned.inventoryReturnSnapshot.skuId === returned.referenceId &&
          returned.inventoryReturnSnapshot.returnedBaseQuantity === returned.quantity
        );
      }
      return returned.inventoryReturnSnapshot === null;
    })
  );
}
